import { lazy } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Routes, SyncHook } from '../routeTypes';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import { ReactNode } from 'react';
import { Permission } from '@act/shared/authorization';
import { DOCKET_PREFIX_PATH } from './DocketRoutes';
import { needsContentRoutes } from './NeedsAdminRoutes';

// Lazy load all admin dashboard components
const IamCenter = lazy(() =>
  import('@act/react-components/admin-dashboard/components/IAM/IamCenter').then(
    module => ({
      default: module.IamCenter,
    })
  )
);
const CustomerView = lazy(() =>
  import('react-components/admin-dashboard/components/Customer/CustomerView').then(
    module => ({
      default: module.CustomerView,
    })
  )
);
const UserAdminTable = lazy(() =>
  import('react-components/admin-dashboard/components/user-creation/UserAdminTable').then(
    module => ({
      default: module.UserAdminTable,
    })
  )
);
const CreateEditUserPage = lazy(() =>
  import('react-components/admin-dashboard/components/user-creation/CreateEditUser/CreateEditUser').then(
    module => ({
      default: module.CreateEditUserPage,
    })
  )
);
const TeamsTable = lazy(() =>
  import('react-components/admin-dashboard/components/teams/TeamsTable').then(
    module => ({
      default: module.TeamsTable,
    })
  )
);
const SurveyTable = lazy(() =>
  import('react-components/admin-dashboard/components/Survey/SurveyTable').then(
    module => ({
      default: module.SurveyTable,
    })
  )
);
const PhoneNumbersTable = lazy(() =>
  import('react-components/admin-dashboard/components/Phone-numbers/PhoneNumbersTable').then(
    module => ({
      default: module.PhoneNumbersTable,
    })
  )
);
const MessageContent = lazy(() =>
  import('react-components/admin-dashboard/components/MessageContent/MessageContent').then(
    module => ({
      default: module.MessageContent,
    })
  )
);
const MessageTemplatesTable = lazy(() =>
  import('react-components/admin-dashboard/components/MessageContent/MessageTemplate/MessageTemplatesTable').then(
    module => ({
      default: module.MessageTemplatesTable,
    })
  )
);
const MessageTemplateCategoriesTable = lazy(() =>
  import('react-components/admin-dashboard/components/MessageContent/MessageTemplateCategory/MessageTemplateCategoriesTable').then(
    module => ({
      default: module.MessageTemplateCategoriesTable,
    })
  )
);
const MultipanelContainer = lazy(() =>
  import('react-components/admin-dashboard/components/Survey/SurveyCreatorTool/components/MultipanelContainer').then(
    module => ({
      default: module.default,
    })
  )
);
const CustomizationThemeView = lazy(() =>
  import('react-components/admin-dashboard/components/CustomizationTheme/CustomizationThemeView').then(
    module => ({
      default: module.CustomizationThemeView,
    })
  )
);
const GuideTargets = lazy(() =>
  import('react-components/admin-dashboard/components/GuideTargets/GuideTargets').then(
    module => ({
      default: module.GuideTargets,
    })
  )
);
const GuideTargetsTable = lazy(() =>
  import('react-components/admin-dashboard/components/GuideTargets/GuideTargetsTable').then(
    module => ({
      default: module.GuideTargetsTable,
    })
  )
);
const CCDExportPage = lazy(() =>
  import('react-components/admin-dashboard/components/CCDExport/CCDExportPage').then(
    module => ({
      default: module.CCDExportPage,
    })
  )
);
export const ADMIN_DASHBOARD_PREFIX_PATH = '/admin-dashboard';

const useSyncHook: SyncHook = () => {};

interface Props {
  children: ReactNode;
}

export const AdminDashboardRoutes = ({ children }: Props) => {
  const canAccessAdminPortal = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ACCESS_ADMIN_PORTAL
  );

  return canAccessAdminPortal ? (
    <>{children}</>
  ) : (
    <Navigate to={DOCKET_PREFIX_PATH} />
  );
};

const GUIDE_TARGETS_ROUTE_PREFIX = `${ADMIN_DASHBOARD_PREFIX_PATH}/guide-targets`;
const CCD_EXPORT_ROUTE = `${ADMIN_DASHBOARD_PREFIX_PATH}/ccd-export`;

const isGuideTargetsAdminRoute = (path: string) =>
  path === GUIDE_TARGETS_ROUTE_PREFIX ||
  path.startsWith(`${GUIDE_TARGETS_ROUTE_PREFIX}/`);

const AdminToolsRouteGate = ({
  routePath,
  children,
}: {
  routePath: string;
  children: ReactNode;
}) => {
  const canAccessAllAdminTools = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ACCESS_ADMIN_TOOLS
  );
  const canUpdateGuideTargets = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_UPDATE_GUIDE_TARGETS
  );
  const canViewGuideTargets = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_VIEW_GUIDE_TARGETS
  );
  const canExportCcd = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_EXPORT_CCD
  );

  const indexRedirectPath =
    canViewGuideTargets && !canAccessAllAdminTools
      ? `${ADMIN_DASHBOARD_PREFIX_PATH}/guide-targets`
      : `${ADMIN_DASHBOARD_PREFIX_PATH}/user-creation`;

  const nonAdminFallbackPath =
    canViewGuideTargets || canUpdateGuideTargets
      ? `${ADMIN_DASHBOARD_PREFIX_PATH}/guide-targets`
      : DOCKET_PREFIX_PATH;

  if (routePath === '/admin-dashboard') {
    return <Navigate to={indexRedirectPath} replace />;
  }

  if (routePath === CCD_EXPORT_ROUTE) {
    return canExportCcd ? (
      <>{children}</>
    ) : (
      <Navigate to={nonAdminFallbackPath} replace />
    );
  }

  if (isGuideTargetsAdminRoute(routePath)) {
    if (canViewGuideTargets || canUpdateGuideTargets) {
      return <>{children}</>;
    }
    return (
      <Navigate
        to={
          canAccessAllAdminTools
            ? `${ADMIN_DASHBOARD_PREFIX_PATH}/user-creation`
            : nonAdminFallbackPath
        }
        replace
      />
    );
  }

  if (!canAccessAllAdminTools) {
    return <Navigate to={nonAdminFallbackPath} replace />;
  }

  return <>{children}</>;
};

const adminRoutesList: Routes = [
  {
    path: '/admin-dashboard',
    element: <></>,
  },
  {
    path: '/admin-dashboard/iam-center',
    element: <IamCenter />,
  },
  {
    path: '/admin-dashboard/customer',
    element: <CustomerView />,
  },
  {
    path: '/admin-dashboard/customer/:id',
    element: <CustomerView />,
  },
  {
    path: '/admin-dashboard/user-creation',
    element: <UserAdminTable />,
  },
  {
    path: '/admin-dashboard/user-creation/edit/:userId',
    element: <CreateEditUserPage />,
  },
  {
    path: '/admin-dashboard/user-creation/create',
    element: <CreateEditUserPage />,
  },
  {
    path: '/admin-dashboard/teams',
    element: <TeamsTable />,
  },
  {
    path: '/admin-dashboard/phone-numbers',
    element: <PhoneNumbersTable />,
  },
  {
    path: '/admin-dashboard/surveys',
    element: <SurveyTable />,
  },
  {
    path: '/admin-dashboard/survey-creator',
    element: <MultipanelContainer />,
  },
  {
    path: '/admin-dashboard/guide-targets',
    element: <GuideTargets />,
  },
  {
    path: '/admin-dashboard/guide-targets/table',
    element: <GuideTargetsTable />,
  },
  {
    path: '/admin-dashboard/guide-targets/table/:yearMo',
    element: <GuideTargetsTable />,
  },
  {
    path: '/admin-dashboard/guide-targets/table/:yearMo/:clientId',
    element: <GuideTargetsTable />,
  },
  {
    path: '/admin-dashboard/message-templates',
    element: (
      <MessageContent>
        <MessageTemplateCategoriesTable />
      </MessageContent>
    ),
  },
  {
    path: '/admin-dashboard/message-templates/categories/:id',
    element: (
      <MessageContent>
        <MessageTemplatesTable />
      </MessageContent>
    ),
  },
  {
    path: '/admin-dashboard/message-templates/categories/:id/edit',
    element: (
      <MessageContent>
        <MessageTemplateCategoriesTable />
      </MessageContent>
    ),
  },
  {
    path: '/admin-dashboard/message-templates/categories/:categoryId/templates/:templateId/edit',
    element: (
      <MessageContent>
        <MessageTemplatesTable />
      </MessageContent>
    ),
  },
  {
    path: '/admin-dashboard/customization-themes',
    element: <CustomizationThemeView />,
  },
  {
    path: '/admin-dashboard/ccd-export',
    element: <CCDExportPage />,
  },
  ...needsContentRoutes,
];

const routes: Routes = adminRoutesList.map(({ path, element }) => ({
  path,
  element: (
    <AdminDashboardRoutes>
      <AdminToolsRouteGate routePath={path}>{element}</AdminToolsRouteGate>
    </AdminDashboardRoutes>
  ),
}));

const useNavigationHooks = () => {
  // Only call useNavigate when we're actually inside a Router context
  // This prevents the error when the hook is called during initial render
  let navigate: ReturnType<typeof useNavigate> | null = null;

  try {
    navigate = useNavigate();
  } catch (error) {
    // If we're not in a Router context, navigate will be null
    // This is expected during initial render
  }

  return {
    toAdminDashboard: () => {
      if (navigate) navigate(ADMIN_DASHBOARD_PREFIX_PATH);
    },
    toIAMView: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/iam-center`);
    },
    toCustomerView: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/customer`);
    },
    toNewCustomerView: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/customer/new`);
    },
    toEditCustomerView: (id: number) => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/customer/${id}`);
    },
    toUserCreation: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/user-creation`);
    },
    toUserCreationEdit: (userId: string) => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/user-creation/edit/${userId}`);
    },
    toUserCreationCreate: () => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/user-creation/create`);
    },
    toTeamsManagement: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/teams`);
    },
    toSurveys: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/surveys`);
    },
    toSurveyCreator: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/survey-creator`);
    },
    toPhoneNumberManagement: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/phone-numbers`);
    },
    toAllMessageCategories: () => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates`, {});
    },
    toMessageCategory: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates/categories/${id}`
        );
    },

    toMessageTemplateEdit: (categoryId: number, templateId: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates/categories/${categoryId}/templates/${templateId}/edit`
        );
    },
    toMessageCategoryEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates/categories/${id}/edit`
        );
    },
    toMessageTemplateCreate: (categoryId: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates/categories/${categoryId}/templates/new`
        );
    },
    toMessageTemplateCategoryCreate: () => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates/categories/new`
        );
    },
    toNeedsContent: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content`);
    },
    toNeedsCategory: (key: string) => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/${key}`);
    },
    toNeedsCategoryEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/categories/${id}/edit`
        );
    },
    toNeedsIssue: () => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/issues`);
    },
    toNeedsIssueEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/issues/${id}/edit`
        );
    },
    toNeedsActivityEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/activities/${id}/edit`
        );
    },
    toNeedsDomainEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/domains/${id}/edit`
        );
    },
    toNeedsZcodeEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/zcodes/${id}/edit`
        );
    },
    toCustomizationThemes: () => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/customization-themes`);
    },
    toCCDExport: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/ccd-export`);
    },
    toGuideTargets: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/guide-targets`);
    },
    toGuideTargetsTable: () => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/guide-targets/table`);
    },
    toGuideTargetsTableYearMo: (yearMo: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/guide-targets/table/${yearMo}`
        );
    },
    toGuideTargetsTableYearMoClientId: (yearMo: number, clientId: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/guide-targets/table/${yearMo}/${clientId}`
        );
    },
  };
};

export const adminDashboardRouteConfig = {
  routes,
  useSyncHook,
  hideNavigationHeader: true,
  useNavigationHooks,
  publicRoute: false,
};
